import { HStack, Text, VStack } from '@chakra-ui/react'
import { WorkflowsContext } from '@context'
import { Form, Formik } from 'formik'
import React, { useContext } from 'react'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { EditDrawerFooter } from '../../../edit-drawer/edit-drawer-footer'
import {
  CLIENT_AGE_OPERATOR_SCHEMA,
  CLIENT_AGE_VALUES_SCHEMA
} from '../shared-filters/client-age-filter'
import { FilterFieldArray } from '../shared-filters/filter-field-array'
import { ALL_WORKFLOW_FILTERS } from '../shared-filters/lib/constants'
import type { FilterCatalogMap } from '../shared-filters/lib/types'

type RouteParams = {
  workflowId: string
  organizationId: string
}

type Props = {
  closeDrawer: () => void
  onSubmit: (trigger: Trigger) => void
}

type Trigger = {
  workflowId: string
  triggerType: string
  config: TriggerConfig
}

type TriggerConfig = {
  filters?: Array<{
    filterType: string
    filterOperator: string
    filterValues: Array<string>
  }>
}

const filterCatalog: FilterCatalogMap = {
  clientAge: ALL_WORKFLOW_FILTERS.clientAge
}

const schema = Yup.object().shape({
  clientAgeOperator: CLIENT_AGE_OPERATOR_SCHEMA,
  clientAgeValues: CLIENT_AGE_VALUES_SCHEMA
})

const determineFilterConfig = (
  filterType: string,
  values: { clientAgeOperator: string; clientAgeValues: Array<string> }
) => {
  switch (filterType) {
    case 'clientAge':
      return {
        filterType,
        filterOperator: values.clientAgeOperator,
        filterValues: values.clientAgeValues
      }
    default:
      return {}
  }
}

const prepareFiltersForSubmission = (values: {
  clientAgeOperator: string
  clientAgeValues: Array<string>
  filterTypes: Array<string>
}) => {
  return values.filterTypes
    .map((type: string) => determineFilterConfig(type, values))
    .filter(
      config => 'filterType' in config && config.filterType !== undefined
    ) as Array<{
    filterType: string
    filterOperator: string
    filterValues: string[]
  }>
}

export const ClientAddedTrigger = ({ closeDrawer, onSubmit }: Props) => {
  const { selectedNode } = useContext(WorkflowsContext)
  const { workflowId } = useParams<RouteParams>()
  const config = selectedNode?.config
  const filters = config?.filters

  const initialValues = {
    clientAgeOperator:
      filters?.find(
        (f: {
          filterType: string
          filterValues: Array<string>
          filterOperator: string
        }) => f.filterType === 'clientAge'
      )?.filterOperator || '',
    clientAgeValues: filters?.find(
      (f: {
        filterType: string
        filterValues: Array<string>
        filterOperator: string
      }) => f.filterType === 'clientAge'
    )?.filterValues || [1],
    filterTypes: filters?.map((f: { filterType: string }) => f.filterType) || []
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={values => {
        const trigger: Trigger = {
          workflowId,
          triggerType: 'client-added-trigger',
          config: {
            filters: prepareFiltersForSubmission(values)
          }
        }
        onSubmit(trigger)
      }}
    >
      {({ values, errors, resetForm: resetFields }) => {
        const resetForm = (updates: {
          clientAgeOperator: string
          clientAgeValues: Array<string>
          filterTypes: Array<string>
        }) => {
          const updatedValues = { ...initialValues, ...updates }
          resetFields({ values: updatedValues })
        }

        return (
          <Form>
            <VStack
              id="vstack-client-added"
              align="flex-start"
              spacing="xsmall"
              w="100%"
            >
              <HStack
                id="hstack-client-added"
                w="100%"
                spacing="small"
                justify="space-between"
              />
              <Text as="b">Filters</Text>
              <FilterFieldArray
                values={values}
                filterCatalog={filterCatalog}
                resetForm={resetForm}
              />
            </VStack>
            <EditDrawerFooter onCancel={closeDrawer} hideDelete />
          </Form>
        )
      }}
    </Formik>
  )
}
